/********************************** WHITEBOPHIR ***************************************************
 * This class was taken and adapted for the EDUAC project from the WHITEBOPHIR project            *
 * https://github.com/lovasoa/whitebophir, keeping the same distribution and license conditions.  *
 **************************************************************************************************
 */
import BoardTool from '@/components/common/ed-board/ed-board-tools/board-tool.interface';
import {BOARD_CONFIG_PARAMETERS} from '@/values/ed-whiteboard.values';
import WboDrawerTools from '@/components/common/ed-board/ed-board-tools/ed-wbo-drawer/wbo-drawer-tools';
import {
    CursorStyle,
    OthersCursorMode
} from '@/components/common/ed-board/ed-board-tools/ed-wbo-drawer/wbo-drawer-config.enum';

type CursorProperties = {
    color: string;
    isFromOtherUser: boolean;
    owner: string;
    ownerIsGuest: boolean;
    size: number;
    x: number;
    y: number;
};

export default class EdWboDrawerCursor {

    private wboDrawerTools: WboDrawerTools;

    // Allocate half of the maximum server updates to cursor updates
    private readonly MIN_CURSOR_UPDATES_INTERVAL_MS;
    private readonly OWN_CURSOR_ID_PREFIX = 'cursor-me';
    private readonly NAMESPACE_URI = 'http://www.w3.org/2000/svg';
    private readonly CURSOR_DELETE_AFTER_MS = 1000 * 3;

    private readonly CIRCLE_DEFAULT_PROPERTIES = {
        radius     : 1
    }

    private readonly BORDERED_CIRCLE_DEFAULT_PROPERTIES = {
        fillColor  : '#FFFFBA',
        strokeColor: '#FFBB00',
        strokeWidth: 3,
        radius     : 5
    }

    private readonly FULL_DEFAULT_PROPERTIES = {
        href              : '/wbo/assets/tools/cursor/cursor-pointer.svg',
        pointerSize       : 20,
        imgBorderSize     : 7,
        textVerticalOffset: 40
    }
    private readonly message: any;
    private readonly boardParameters: { [key: string]: any } = {};
    private readonly othersCursorStyle: CursorStyle;
    private readonly othersCursorMode: OthersCursorMode;
    private readonly showOtherCursors: boolean = false;
    private readonly alwaysShowCursors: boolean = false;
    private readonly boardName: string;

    private lastCursorUpdate = 0;
    private sending = true;
    private cursorsElem: SVGGElement;
    private svg: SVGSVGElement;



    private cachedCursors: { [index: string]: SVGGraphicsElement; } = {};

    constructor(tools: WboDrawerTools) {        
        this.wboDrawerTools = tools;
        this.boardParameters = this.wboDrawerTools.boardParameters;
        this.othersCursorMode = this.wboDrawerTools.othersCursorMode;
        this.othersCursorStyle = this.wboDrawerTools.othersCursorStyle ?? CursorStyle.SolidCircle;
        this.showOtherCursors = this.othersCursorMode != OthersCursorMode.None
        this.alwaysShowCursors = this.wboDrawerTools.alwaysShowCursors;
        this.MIN_CURSOR_UPDATES_INTERVAL_MS = this.boardParameters[BOARD_CONFIG_PARAMETERS.MAX_EMIT_COUNT_PERIOD]
            / this.boardParameters[BOARD_CONFIG_PARAMETERS.MAX_EMIT_COUNT];
        this.message = {
            type : 'update',
            x    : 0,
            y    : 0,
            color: this.wboDrawerTools.getColor(),
            size : this.wboDrawerTools.getSize()
        };
        this.boardName = this.wboDrawerTools.currentBoard;
        this.cursorsElem = this.wboDrawerTools.cursors;
        this.svg = this.wboDrawerTools.svg;

        this.wboDrawerTools.register(this.cursorTool);
        this.wboDrawerTools.addToolListeners(this.cursorTool);
    }

    private handleMarker: Function = (x: number, y: number) => {
        this.message.x = x;
        this.message.y = y;
        this.message.color = this.wboDrawerTools.getColor();
        this.message.size = this.wboDrawerTools.getSize();
        this.updateMarker();
    }

    /**
     * Own cursor is just a filled circle of color and radius equals to the selected color and stroke size respectively.
     * Other people's cursor is an arrow with the full name of the user.
     *
     * Messages originating from self don't have the 'socket' property. That's how we identify if the message
     * triggering the execution fo this draw function comes from other users or comes from self.
     * @param message
     */
    private draw: Function = (message) => {                
        const cursorProperties: CursorProperties = {
            color          : message.color,
            isFromOtherUser: message.socket as boolean,
            owner          : message.user,
            ownerIsGuest   : message.isGuest,
            size           : message.size,
            x              : message.x,
            y              : message.y
        };

        if (cursorProperties.isFromOtherUser && !this.showOtherCursors) {
            return true;
        }

        const boardId = `cursor-${cursorProperties.isFromOtherUser ? message.socket : 'me'}-${this.boardName}`;
        let cursor = this.getCursor(boardId, cursorProperties);
        if (!cursor) {
            // If cursor is null is because the invocation was triggered by an incoming socket message, and this
            // instance was configured to not draw other's cursors.
            return true;
        }
        this.updateCursor(cursor, cursorProperties);
        return true;

    }

    private getCursor: Function = (cursorId: string, cursorProperties: CursorProperties) => {
        return this.cachedCursors[cursorId] ?? document.getElementById(cursorId) ?? this.createCursor(cursorId, cursorProperties);
    }
    private onSizeChange: Function = (size: number) => {
        this.message.size = size;
        this.updateMarker();
    }

    private updateMarker() {
        if (!this.wboDrawerTools.showMarker || !this.wboDrawerTools.showMyCursor) {
            return;
        }
        // throttle local cursor updates
        let curTime = Date.now();
        if (curTime - this.lastCursorUpdate > this.MIN_CURSOR_UPDATES_INTERVAL_MS
            && (this.sending || this.wboDrawerTools.curTool.showMarker)) {
            this.wboDrawerTools.drawAndSend(this.message, this.cursorTool);
            this.lastCursorUpdate = curTime;
        } else {
            this.draw(this.message);
        }
    }

    private createCursor(cursorId: string, cursorProperties: CursorProperties): SVGGraphicsElement {
        let cursor: SVGGraphicsElement = null;
        if (cursorId.startsWith(this.OWN_CURSOR_ID_PREFIX)) {
            cursor = this.createOwnCursor(cursorId);
        }
        else if (this.showOtherCursors && cursorProperties.owner) {
            cursor = this.createOtherCursor(cursorId, cursorProperties);
        }
        if(this.alwaysShowCursors) {
            cursor?.setAttribute('style', 'visibility: visible !important');
        }
        
        this.cachedCursors[cursorId] = cursor;
        return cursor;
    }

    private createOwnCursor(cursorId: string): SVGCircleElement {
        let cursor = this.createCursorCircle(cursorId);
        setTimeout(() => {
            // this.cursorsElem.removeChild(cursor);
            this.deleteCursor(cursorId);
        }, this.CURSOR_DELETE_AFTER_MS);
        return cursor;
    }

    private deleteCursor(cursorId: string): void {
        const cursor = this.cachedCursors[cursorId];
        if (cursor) {
            this.cursorsElem.removeChild(cursor);
        }
        delete this.cachedCursors[cursorId];
    }

    private createOtherCursor(cursorId: string, cursorProperties: CursorProperties): SVGGraphicsElement {
        let cursor: SVGGraphicsElement = null;
        if (this.othersCursorMode == OthersCursorMode.HostOnly && cursorProperties.ownerIsGuest) {
            // Ignore other cursors if they are guest users
            return null;
        }

        if (this.othersCursorStyle == CursorStyle.Full) {
            return this.createCursorFull(cursorId, cursorProperties.owner);
        }

        if (this.othersCursorStyle == CursorStyle.BorderedCircle) {
            cursor = this.createCursorBorderedCircle(cursorId);
        }
        else if (this.othersCursorStyle == CursorStyle.SolidCircle) {
            cursor = this.createCursorCircle(cursorId);
            cursor.setAttributeNS(null, 'fill', cursorProperties.color);
        }

        setTimeout(() => {
            if(!this.alwaysShowCursors) {
                this.deleteCursor(cursorId);
            }
        }, this.CURSOR_DELETE_AFTER_MS);

        return cursor;
    }

    private createCursorFull(cursorId: string, user: string): SVGGElement {
        //To Group pointer and User Name
        const cursorContainer: SVGGElement = document.createElementNS(this.NAMESPACE_URI, 'g');

        //Pointer image
        const cursorImage = document.createElementNS(this.NAMESPACE_URI, 'image');

        //User's Name
        const cursorText = document.createElementNS(this.NAMESPACE_URI, 'text');
        const textNode = document.createTextNode(this.capitalize(user));

        cursorImage.setAttributeNS('http://www.w3.org/1999/xlink' , 'href', this.FULL_DEFAULT_PROPERTIES.href);
        cursorImage.setAttributeNS( null, 'width', String(this.FULL_DEFAULT_PROPERTIES.pointerSize));
        cursorImage.setAttributeNS( null, 'height', String(this.FULL_DEFAULT_PROPERTIES.pointerSize));
        cursorImage.setAttribute('style', 'opacity: 0.5');

        cursorText.setAttributeNS(null, 'y', String(this.FULL_DEFAULT_PROPERTIES.textVerticalOffset));
        cursorText.appendChild(textNode);

        cursorContainer.setAttributeNS(null, 'id', cursorId);
        cursorContainer.appendChild(cursorImage);//add pointer and user name
        cursorContainer.appendChild(cursorText);
        this.cursorsElem.appendChild(cursorContainer);

        const xPositionRelatedToText = cursorContainer.lastElementChild.getBoundingClientRect().width / 2
            - (this.FULL_DEFAULT_PROPERTIES.pointerSize / 2) ;
        const pointerImage = cursorContainer.firstElementChild;
        pointerImage.setAttributeNS( null, 'x', String(xPositionRelatedToText));
        return cursorContainer;
    }

    private createCursorBorderedCircle(id: string): SVGCircleElement {
        const circleCursor = this.createCursorCircle(id);
        circleCursor.setAttributeNS(null, 'cx', '0');
        circleCursor.setAttributeNS(null, 'cy', '0');
        circleCursor.setAttributeNS(null, 'r', String(this.BORDERED_CIRCLE_DEFAULT_PROPERTIES.radius));
        circleCursor.setAttributeNS(null, 'stroke', this.BORDERED_CIRCLE_DEFAULT_PROPERTIES.strokeColor);
        circleCursor.setAttributeNS(null, 'stroke-width', String(this.BORDERED_CIRCLE_DEFAULT_PROPERTIES.strokeWidth));
        circleCursor.setAttributeNS(null, 'fill', this.BORDERED_CIRCLE_DEFAULT_PROPERTIES.fillColor);
        // Unscale
        circleCursor.style.transform = `scale(${1/this.wboDrawerTools.getScale()})`;
        this.cursorsElem.appendChild(circleCursor);
        return circleCursor;
    }


    private createCursorCircle(cursorId: string): SVGCircleElement {
        let circleCursor: SVGCircleElement = document.createElementNS(this.NAMESPACE_URI, 'circle');
        circleCursor.setAttributeNS(null, 'id', cursorId);
        circleCursor.setAttributeNS(null, 'class', 'opcursor');
        circleCursor.setAttributeNS(null, 'cx', '0');
        circleCursor.setAttributeNS(null, 'cy', '0');
        circleCursor.setAttributeNS(null, 'r', String(this.CIRCLE_DEFAULT_PROPERTIES.radius));
        this.cursorsElem.appendChild(circleCursor);

        return circleCursor;
    }

    private updateCursor(cursor: SVGGraphicsElement, cursorProperties: CursorProperties): void {
        if (cursor instanceof SVGCircleElement) {
            // Move to (x, y)
            let x = cursorProperties.x;
            let y = cursorProperties.y;


            cursor.style.transform = `translate(${x}px, ${y}px)`;
            if (this.wboDrawerTools.isIE) {
                cursor.setAttributeNS(null, 'transform', `translate(${cursorProperties.x} ${cursorProperties.y})`);
            }

            if (this.othersCursorStyle == CursorStyle.BorderedCircle && cursorProperties.isFromOtherUser) {
                // Never change the color nor the size for this cursor style
                cursor.style.transform += ` scale(${1 / this.wboDrawerTools.getScale()})`;
                return;
            }

            cursor.setAttributeNS(null, 'fill', cursorProperties.color);
            if (!cursorProperties.isFromOtherUser) {
                cursor.setAttributeNS(null, 'r', String(cursorProperties.size / 2));
            }

        }
        else if (cursor instanceof SVGGraphicsElement) {
            // It is a Full cursor style
            let x = cursorProperties.x;
            if (this.othersCursorStyle == CursorStyle.Full) {
                // Full cursor style requires a slight x position correction
                x += this.FULL_DEFAULT_PROPERTIES.imgBorderSize - cursor.getBoundingClientRect().width / 2;
            }
            cursor.style.transform = `translate(${x}px, ${cursorProperties.y}px)`;
            if (this.wboDrawerTools.isIE) {
                cursor.setAttributeNS(null, 'transform', `translate(${x} ${cursorProperties.y})`);
            }

            // Also, we change the text color
            cursor.setAttributeNS(null, 'fill', cursorProperties.color);
        }

    }

    private capitalize(input: string): string {
        return input.toLowerCase().split(' ')
            .map(token => token.charAt(0).toUpperCase() + token.slice(1))
            .join(' ');
    }


    private deleteMyCursor() {
        this.deleteCursor(`${this.OWN_CURSOR_ID_PREFIX}-${this.boardName}`);
    }


    private cursorTool: BoardTool = {
        name        : 'Cursor',
        listeners   : {
            press  : () => this.sending = this.showOtherCursors,
            move   : this.handleMarker,
            release: () => this.sending = true,
            leave: () => this.deleteMyCursor()
        },
        onSizeChange: this.onSizeChange,
        draw        : this.draw,
        mouseCursor : 'crosshair',
        icon        : '/assets/tools/pencil/icon.svg'
    };
}

